import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
  CpaSettingUpdateRequestType,
  getApiErrorHttpStatus,
  patchApiV5FirmsAdminCpaSettingsKey,
} from '@liscio/api';
import {
  FILE_INTELLIGENCE_ENABLED_KEY,
  FILE_INTELLIGENCE_SETTING_QUERY_KEY,
  useFileIntelligenceSetting as useSharedFileIntelligenceSetting,
} from '@liscio/common';
import { AlertMessage } from 'utilities/utils';

export { FILE_INTELLIGENCE_ENABLED_KEY, FILE_INTELLIGENCE_SETTING_QUERY_KEY };

const reportSettingReadError = (error: unknown) => {
  Sentry.captureException(error, {
    tags: {
      component: 'FileIntelligenceSetting',
      operation: 'fetchFileIntelligenceSetting',
    },
    extra: { status: getApiErrorHttpStatus(error) },
  });
};

const reportSettingWriteError = (error: unknown) => {
  if (axios.isCancel(error)) return;
  Sentry.captureException(error, {
    tags: {
      component: 'FileIntelligenceSetting',
      operation: 'updateFileIntelligenceSetting',
    },
    extra: { status: getApiErrorHttpStatus(error) },
  });
};

// The setting is stored as a boolean, but the generated contract types the
// value as string | boolean | string[].
const toEnabled = (value: unknown) =>
  typeof value === 'boolean' ? value : String(value) === 'true';

async function updateFileIntelligenceSetting(enabled: boolean) {
  const response = await patchApiV5FirmsAdminCpaSettingsKey(
    FILE_INTELLIGENCE_ENABLED_KEY,
    { type: CpaSettingUpdateRequestType.boolean, value: enabled }
  );
  return toEnabled(response.value);
}

/**
 * Hook to fetch the File Intelligence firm setting (shared read from
 * `@liscio/common`; failed reads are reported to Sentry here).
 */
export function useFileIntelligenceSetting() {
  return useSharedFileIntelligenceSetting({ onError: reportSettingReadError });
}

/**
 * Hook to update the File Intelligence firm setting
 */
export function useUpdateFileIntelligenceSetting() {
  const queryClient = useQueryClient();

  // A setting GET that read the old value must not land after the PATCH and
  // overwrite the saved value. Cancelling aborts the request (the shared read
  // forwards React Query's AbortSignal) and reverts the query to its state
  // before that fetch; a cancellation is not reported to Sentry.
  const cancelSettingReads = () =>
    queryClient.cancelQueries({ queryKey: FILE_INTELLIGENCE_SETTING_QUERY_KEY });

  return useMutation({
    mutationFn: updateFileIntelligenceSetting,
    // Awaited before the PATCH is sent, so no read started earlier can
    // finish after it.
    onMutate: cancelSettingReads,
    onSuccess: async (data) => {
      // Covers a read started while the PATCH was in flight.
      await cancelSettingReads();
      queryClient.setQueryData(FILE_INTELLIGENCE_SETTING_QUERY_KEY, data);
    },
    onError: (error: any) => {
      // The recovery read below can succeed, so the failed write must be
      // reported on its own.
      reportSettingWriteError(error);
      // A cancelled read left the cached value unconfirmed; reload it.
      queryClient.invalidateQueries({
        queryKey: FILE_INTELLIGENCE_SETTING_QUERY_KEY,
      });
      AlertMessage(
        'error',
        error.message || 'Failed to update File Intelligence setting',
        3000
      );
    },
  });
}

/**
 * Returns whether File Intelligence is enabled for the current firm.
 *
 * A 404 for the setting resolves as enabled (the default). After a failed
 * refetch React Query keeps the last known value. With no known value
 * (loading, or a failed first load) File Intelligence is treated as disabled
 * so gated controls stay hidden instead of sending requests the backend
 * rejects.
 */
export function useFileIntelligenceEnabled() {
  const { data, isLoading, error } = useFileIntelligenceSetting();

  return {
    isFileIntelligenceEnabled: data === true,
    isLoading,
    error,
  };
}
